import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { Challenge } from "./ChallengeBoard";
import ChallengeVoteButton from "./ChallengeVoteButton";
import { Crown } from "lucide-react";

interface ChallengeCardProps {
  challenge: Challenge;
  onClick: () => void;
  isClaimant?: boolean;
}

const ChallengeCard: React.FC<ChallengeCardProps> = ({ challenge, onClick, isClaimant }) => {
  const getDifficultyBorder = (difficulty: string) => {
    switch (difficulty) {
      case "beginner":
        return "border-l-green-500";
      case "intermediate":
        return "border-l-yellow-500";
      case "advanced":
        return "border-l-red-500";
      default:
        return "border-l-muted";
    }
  };

  const getDifficultyLabel = (difficulty: string) => {
    switch (difficulty) {
      case "beginner":
        return "Beginner friendly";
      case "intermediate":
        return "Some experience needed";
      case "advanced":
        return "Advanced skills required";
      default:
        return difficulty;
    }
  };

  return (
    <Card
      className={cn(
        "cursor-pointer transition-all group border-l-4 bg-card/80 hover:bg-card hover:shadow-lg",
        getDifficultyBorder(challenge.difficulty),
        isClaimant && "ring-2 ring-amber-500/50"
      )}
      onClick={onClick}
    >
      <CardContent className="p-5">
        <div className="flex gap-3">
          {/* Vote Button */}
          <ChallengeVoteButton
            challengeId={challenge.id}
            initialScore={challenge.vote_score ?? 0}
            compact
          />

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Title with claimant indicator */}
            <div className="flex items-start gap-2 mb-2">
              <h4 className="font-semibold text-foreground text-base line-clamp-2 group-hover:text-primary transition-colors flex-1">
                {challenge.title}
              </h4>
              {isClaimant && (
                <span className="shrink-0 flex items-center gap-1 text-xs text-amber-600 bg-amber-500/20 px-1.5 py-0.5 rounded-full">
                  <Crown className="h-3 w-3" />
                  Yours
                </span>
              )}
            </div>

            {/* One-liner */}
            {challenge.one_liner && (
              <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                {challenge.one_liner}
              </p>
            )}

            {/* Footer: difficulty + single tag */}
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">
                {getDifficultyLabel(challenge.difficulty)}
              </span>
              {challenge.tags?.[0] && (
                <Badge variant="secondary" className="text-xs">
                  {challenge.tags[0]}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ChallengeCard;
